import {Table, TableContainer, Tbody, Td, Th, Thead, Tr, Button, Flex} from '@chakra-ui/react';
import React, {useState, useEffect, useRef} from 'react';
import {getServiceType, getBitwiseTypes} from '../../../helpers/ServiceTypeDictionary';
import Select from 'react-select';
import {useClient} from '../../../views/Maintenance/ClientMaintenance';
import {get, post, getFile, queryStringBuilder} from '../../../service/ApiService';
import {Client, Report} from '../../../service/Urls';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import {SubmitButton} from '../../General/FormInputs';
import BoxCard from '../../General/BoxCard';
import ErrorHelper from '../../../service/ErrorHelper';
import {useQueryClient} from 'react-query';

const customStyles = {
  container: provided => ({
    ...provided,
    width: '250px',
    color: 'black',
  }),
};

const SearchSelect = props => {
  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue?.value}
      render={({field: {onChange, value, ref}}) => {
        return (
          <Select
            inputref={ref}
            options={props.options ?? []}
            value={props.options.find(c => c?.value === value)}
            onChange={val => onChange(val && val.value)}
            defaultValue={props.defaultValue}
            styles={customStyles}
            maxOptions={5}
            isClearable
            isDisabled={!(props.options && props.options.length > 0)}
            menuPortalTarget={document.body}
            menuPosition={'fixed'}
          />
        );
      }}
    />
  );
};

export const CustomDocs = () => {
  const {client} = useClient();
  const [options, setOptions] = useState([]);
  const [pdfUrl, setPdfUrl] = useState('');
  const linkRef = useRef<HTMLAnchorElement>(null);
  const [toastResponse, setToastResponse] = useState(undefined);

  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: {errors},
  } = useForm();

  useEffect(() => {
    (async () => {
      const response = await get(Report.OVERRIDE_LIST);

      const serviceTypeOptions = [];

      // respose.data looks like this: [{reportType: 1, reportOverrides: [{fileName: "Survey.rdlx", serviceType: 1}]}]
      // We want it to look like this: [{serviceType: 1, overrides: [[{value: "Survey.rdlx", label: "Survey.rdlx"}], [], [], []]}]
      if (response && response.data) {
        for (const {reportType, reportOverrides} of response.data) {
          for (const {reportId, fileName, serviceType: bServiceType} of reportOverrides) {
            const serviceTypes = getBitwiseTypes(bServiceType);
            for (const serviceType of serviceTypes) {
              // Check if the service type is already in the array
              let serviceTypeIndex = serviceTypeOptions.findIndex(({serviceType: sType}) => serviceType === sType);
              if (serviceTypeIndex === -1) {
                // If not, add it
                serviceTypeOptions.push({serviceType, overrides: [[], []]});
                serviceTypeIndex = serviceTypeOptions.length - 1;
              }
              // Add the report to the correct arrays
              serviceTypeOptions[serviceTypeIndex].overrides[reportType - 1].push({value: reportId, label: fileName});
            }
          }
        }

        setOptions(serviceTypeOptions.sort((a, b) => a.serviceType - b.serviceType));
      }
    })();
  }, []);

  useEffect(() => {
    const newFormState = options.reduce((acc, {serviceType}) => {
      acc[serviceType] = {
        survey: findReportOverride(1, serviceType)?.value,
        letter: findReportOverride(2, serviceType)?.value,
      };

      return acc;
    }, {});

    reset(newFormState);
  }, [client, options]);

  console.log(getValues());

  const findReportOverride = (reportType, serviceType) => {
    const id = client?.reportOverrides?.find(report => {
      const reportServiceTypes = getBitwiseTypes(report.serviceType);
      return report.reportType == reportType && reportServiceTypes.includes(serviceType);
    });

    return id && {value: id.reportId, label: id.fileName};
  };

  const saveDocs = async data => {
    const reportIds = [];
    const newReportOverrides = [];
    for (const [serviceType, overrides] of Object.entries(data)) {
      for (const [name, reportId] of Object.entries(overrides)) {
        if (reportId) {
          reportIds.push(reportId);
          newReportOverrides.push({
            fileName: '',
            reportId,
            reportType: {survey: 1, letter: 2}[name],
            serviceType,
          });
        }
      }
    }

    const request = {
      clientId: client?.id,
      reportIds,
    };

    const response = await post(Client.SET_OVERRIDES + '/', request);

    if (!response.isSuccess) {
      setToastResponse({response: response, errorMessage: response.error});
      return;
    }

    queryClient.invalidateQueries('client');
    setToastResponse({response: response, successMessage: 'Successfully updated overrides'});
  };

  const printReport = async (reportId: string) => {
    const params = {
      clientId: client?.id,
      reportId,
    };

    const url = queryStringBuilder(`${Report.CLIENT_SAMPLE}`, params);
    // const url = queryStringBuilder(SurveyPrints.reportbuttonFile, params);
    const response = await getFile(url);
    if (response.error) {
      setToastResponse({response: {error: 1}, errorMessage: 'Error getting report: ' + response.error});
      return;
    }

    const fileURL = window.URL.createObjectURL(response);

    if (!response.error) {
      setPdfUrl(fileURL);
    }
  };

  useEffect(() => {
    if (pdfUrl.length > 0) linkRef.current?.click();
  }, [pdfUrl]);

  // Library used. https://react-select.com/home

  return (
    <form onSubmit={handleSubmit(saveDocs)}>
      <a href={pdfUrl} target="_blank" rel="noopener noreferrer" ref={linkRef} style={{display: 'none'}} />
      {client?.id && (
        <BoxCard mr="3">
          <TableContainer width={{base: '100%', '2xl': '90%'}}>
            <Table variant="striped" border="solid 2px #0c5f78" colorScheme="mint" size={'md'}>
              <Thead>
                <Tr border="solid 2px #0c5f78">
                  <Th>Report Type</Th>
                  <Th>Survey Name</Th>
                  <Th>Letter Name</Th>
                </Tr>
              </Thead>
              <Tbody>
                {options.map(({serviceType, overrides}) => {
                  return (
                    <Tr key={serviceType}>
                      <Td>{getServiceType(serviceType).key}</Td>
                      <Td>
                        <Flex gap={3}>
                          <SearchSelect
                            placeholder="None Selected"
                            id={serviceType + '.survey'}
                            name={serviceType + '.survey'}
                            control={control}
                            errors={errors}
                            options={overrides[0]}
                            defaultValue={findReportOverride(1, serviceType)}
                            onChange={e => null}
                          />

                          <Button onClick={() => printReport(getValues()[serviceType]?.survey)} colorScheme="teal">
                            Print
                          </Button>
                        </Flex>
                      </Td>
                      <Td>
                        <Flex gap={3}>
                          <SearchSelect
                            placeholder="None Selected"
                            id={serviceType + '.letter'}
                            name={serviceType + '.letter'}
                            control={control}
                            errors={errors}
                            options={overrides[1]}
                            defaultValue={findReportOverride(2, serviceType)}
                            onChange={e => null}
                          />

                          <Button onClick={() => printReport(getValues()[serviceType]?.letter)} colorScheme="teal">
                            Print
                          </Button>
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
            <SubmitButton name="Save" mt="3" />
          </TableContainer>
        </BoxCard>
      )}
      <ErrorHelper toastResponse={toastResponse} />
    </form>
  );
};
